/**
 * Wrapper minimal de la Web Vibration API pour le feedback haptique mobile.
 *
 * - No-op silencieux si `navigator.vibrate` n'existe pas (desktop, navigateurs
 *   non compatibles, iOS Safari).
 * - Respecte un toggle global persistant en localStorage (cohérent avec
 *   l'audio dans `src/lib/audio.ts`).
 * - Patterns courts pré-définis pour les cas usuels (success, error, …).
 *
 * Usage :
 *   import { haptic } from '@/lib/haptic';
 *   haptic.success();   // tap court
 *   haptic.error();     // double-tap fort
 *   haptic.combo();     // burst
 *   haptic.vibrate(50); // custom durée ms
 *   haptic.vibrate([20, 40, 20]); // pattern on/off ms
 */
import { useEffect, useReducer } from 'react';
const STORAGE_KEY = 'alfalympics:haptic-enabled';
let enabled = (() => {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        return v === null ? true : v === '1';
    }
    catch {
        return true;
    }
})();
const listeners = new Set();
function persist(value) {
    try {
        localStorage.setItem(STORAGE_KEY, value ? '1' : '0');
    }
    catch {
        /* ignore quota / private mode */
    }
}
function canVibrate() {
    return typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';
}
function doVibrate(pattern) {
    if (!enabled || !canVibrate())
        return;
    try {
        navigator.vibrate(pattern);
    }
    catch {
        /* certains navigateurs jettent si l'utilisateur n'a pas encore interagi */
    }
}
export const haptic = {
    /** Tap court — bonne action, validation. */
    success: () => doVibrate(15),
    /** Double-tap fort — erreur, pénalité. */
    error: () => doVibrate([30, 40, 30]),
    /** Burst — combo, palier débloqué. */
    combo: () => doVibrate([10, 30, 10, 30, 40]),
    /** Tic discret — feedback constant (tick metronome, etc.). */
    tick: () => doVibrate(8),
    /** Vibration custom. Ignore silencieusement si pas supporté. */
    vibrate: (pattern) => doVibrate(pattern),
    /** État courant du toggle (lecture). */
    isEnabled: () => enabled,
    /** Active/désactive globalement. Persisté en localStorage. */
    setEnabled: (value) => {
        enabled = value;
        persist(value);
        if (!value && canVibrate()) {
            try {
                navigator.vibrate(0);
            }
            catch {
                /* ignore */
            }
        }
        listeners.forEach((fn) => fn());
    },
    /** Indique si le device supporte du tout la vibration. */
    isSupported: canVibrate,
};
/**
 * Hook React pour binder un composant aux changements du toggle haptic
 * (utilisé par le bouton de paramètre dans le header).
 */
export function useHapticSettings() {
    const [, force] = useReducer((c) => c + 1, 0);
    useEffect(() => {
        listeners.add(force);
        return () => { listeners.delete(force); };
    }, []);
    return {
        enabled,
        supported: canVibrate(),
        setEnabled: haptic.setEnabled,
    };
}
